import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface UserReward {
  id: string; rewardId: string;
  name: string; description: string; icon: string;
  usedAt: string | null;
}

export function RewardsBox() {
  const qc = useQueryClient();
  const mine = useQuery({ queryKey: ['user-rewards'], queryFn: () => api<UserReward[]>('/api/user-rewards') });
  const [selected, setSelected] = useState<UserReward | null>(null);

  const use = useMutation({
    mutationFn: (id: string) => api(`/api/user-rewards/${id}/use`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-rewards'] });
      setSelected(null);
    },
  });

  const owned = (mine.data ?? []).filter((u) => !u.usedAt).slice(0, 12);

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border-soft bg-bg-raised p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-neutral-400">İtem Kutusu</span>
        <span className="font-pixel text-[9px] text-neutral-500">{owned.length} / 12</span>
      </div>
      <div className="grid grid-cols-6 gap-2">
        {Array.from({ length: 12 }, (_, i) => {
          const r = owned[i];
          return (
            <button
              key={i}
              disabled={!r}
              onClick={() => r && setSelected(r)}
              className={`flex aspect-square items-center justify-center rounded-md border text-xl transition ${
                r
                  ? 'border-brand-500/40 bg-bg-elevated hover:border-brand-400 hover:bg-bg-elevated/70 cursor-pointer'
                  : 'border-border-soft/40 bg-bg-base/40 opacity-40'
              }`}
              title={r?.name}
            >
              {r?.icon ?? ''}
            </button>
          );
        })}
      </div>
      <Link
        to="/reward"
        className="mt-1 self-end text-xs text-brand-400 hover:text-brand-500"
      >
        see more →
      </Link>

      {selected && (
        <RewardPanel reward={selected} onClose={() => setSelected(null)} onUse={() => use.mutate(selected.id)} />
      )}
    </div>
  );
}

function RewardPanel({ reward, onClose, onUse }: { reward: UserReward; onClose: () => void; onUse: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-80 rounded-xl border border-border-soft bg-bg-raised p-6 shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-col items-center gap-3">
          <div className="text-6xl">{reward.icon}</div>
          <h3 className="text-lg font-semibold text-neutral-100">{reward.name}</h3>
          <p className="text-center text-sm text-neutral-400">{reward.description || '—'}</p>
          <button onClick={onUse}
                  className="mt-2 w-full rounded-md bg-brand-500 py-2 font-medium text-white hover:bg-brand-600">
            Kullan
          </button>
        </div>
      </div>
    </div>
  );
}
